import {
  ReturnDetails,
  ReturnProductVariant,
  ReturnProductVariantSizeItem
} from "../returns.types";

export interface ReturnPackEntry {
  kind: "pack";
  packGroupId: string;
  packTitle: string | null;
  /**
   * Flat list of size-item rows that belong to this pack, paired with the
   * productVariant they were nested under in the API response. Pack items
   * can come from different productVariants (one per slot), so we keep
   * the parent in scope to render thumb/name/color per row.
   */
  items: Array<{
    productVariant: ReturnProductVariant;
    item: ReturnProductVariantSizeItem;
  }>;
}

export interface ReturnStandaloneEntry {
  kind: "standalone";
  productVariant: ReturnProductVariant;
  /** Only items of this productVariant that don't belong to any pack. */
  items: ReturnProductVariantSizeItem[];
}

export type GroupedReturnEntry = ReturnPackEntry | ReturnStandaloneEntry;

/**
 * Walks `returnDetails.returnProductVariants` and folds rows that share a
 * `packGroupId` into a single `pack` entry — so the admin return modal
 * renders the pack as one card instead of one row per slot. Items without
 * a packGroupId keep their original productVariant grouping.
 */
export const groupReturnVariantsByPack = (
  returnDetails: ReturnDetails
): GroupedReturnEntry[] => {
  const packEntries = new Map<string, ReturnPackEntry>();
  const standaloneByVariant = new Map<string, ReturnStandaloneEntry>();
  const insertionOrder: GroupedReturnEntry[] = [];

  for (const productVariant of returnDetails.returnProductVariants) {
    for (const item of productVariant.items) {
      if (item.packGroupId) {
        let entry = packEntries.get(item.packGroupId);
        if (!entry) {
          entry = {
            kind: "pack",
            packGroupId: item.packGroupId,
            packTitle: item.packTitle ?? null,
            items: []
          };
          packEntries.set(item.packGroupId, entry);
          insertionOrder.push(entry);
        }
        entry.items.push({ productVariant, item });
        if (!entry.packTitle && item.packTitle) {
          entry.packTitle = item.packTitle;
        }
        continue;
      }

      const key = productVariant.productVariantId;
      let entry = standaloneByVariant.get(key);
      if (!entry) {
        entry = { kind: "standalone", productVariant, items: [] };
        standaloneByVariant.set(key, entry);
        insertionOrder.push(entry);
      }
      entry.items.push(item);
    }
  }

  return insertionOrder;
};
